/**
 * W65 — /opportunities — Opportunity List Page
 *
 * Numbers lead: total net value, forecast bucket distribution, override count.
 * Table: name, account, owner, net value, forecast bucket pill, CP{N}, est close, days open.
 * Sort by net value desc. Filter pills: bucket, owner, account, status.
 */
import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { getSession } from "@/lib/session";
import { Header, Footer } from "@/components/Shell";

const FORECAST_HEX: Record<string, string> = {
  Commit:      "#16A34A",
  Upside:      "#16A34A",
  "Best Case": "#EAB308",
  Pipeline:    "#EA580C",
  Omit:        "#DC2626",
};

const BUCKETS = ["Commit", "Upside", "Best Case", "Pipeline", "Omit"];

function fmtCurrency(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}

function daysOpen(createdAt: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(createdAt).getTime()) / 86_400_000));
}

function inferForecastBucket(opp: any): string {
  if (opp.forecast_category_override) return opp.forecast_category_override;
  const score = Number(opp.composite_score_current ?? 0);
  if (score >= 80) return "Commit";
  if (score >= 65) return "Upside";
  if (score >= 50) return "Best Case";
  if (score >= 30) return "Pipeline";
  return "Omit";
}

function computeNetValue(opp: any): number {
  // Lines total is typically returned from the server view or computed manually
  // Fall back to 0 if not present
  const gross = Number(opp.gross_value ?? 0);
  const disc = Number(opp.subtotal_discount_pct ?? 0);
  return gross * (1 - disc / 100);
}

export default function OpportunitiesIndex() {
  const [opps, setOpps] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterBucket, setFilterBucket] = useState<string>("");
  const [filterOwner, setFilterOwner] = useState<string>("");
  const [filterAccount, setFilterAccount] = useState<string>("");
  const [, setLoc] = useLocation();

  const session = getSession();
  const token = session ? (session as any).access_token ?? (session as any).token ?? "" : "";
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ status: "open" });
    fetch(`/api/opportunities?${params}`, { headers: authHeader })
      .then((r) => r.json())
      .then((j) => setOpps(j?.opportunities ?? []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // Derive forecast bucket and net value for each opp
  const enriched = opps.map((o) => ({
    ...o,
    _bucket: inferForecastBucket(o),
    _netValue: computeNetValue(o),
  }));

  // Filter
  let filtered = enriched;
  if (filterBucket) filtered = filtered.filter((o) => o._bucket === filterBucket);
  if (filterOwner) filtered = filtered.filter((o) => (o.primary_owner_name ?? "").toLowerCase().includes(filterOwner.toLowerCase()));
  if (filterAccount) filtered = filtered.filter((o) => (o.account_name ?? "").toLowerCase().includes(filterAccount.toLowerCase()));

  // Sort by net value desc
  filtered = [...filtered].sort((a, b) => b._netValue - a._netValue);

  // Numbers lead
  const totalNet = filtered.reduce((s, o) => s + o._netValue, 0);
  const overrideCount = filtered.filter((o) => o.forecast_category_override).length;

  const bucketDist = BUCKETS.map((b) => ({
    label: b,
    count: filtered.filter((o) => o._bucket === b).length,
    value: filtered.filter((o) => o._bucket === b).reduce((s, o) => s + o._netValue, 0),
  }));

  const thSt: React.CSSProperties = {
    fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase",
    letterSpacing: "0.08em", padding: "8px 12px", textAlign: "left",
    borderBottom: "1px solid #E5E5E7", background: "#F5F5F4", whiteSpace: "nowrap",
  };
  const tdSt: React.CSSProperties = {
    fontSize: 13, color: "#2A2A2E", padding: "10px 12px",
    borderBottom: "1px solid #EFEFEE", verticalAlign: "middle",
  };

  return (
    <div style={{ minHeight: "100vh", background: "#FAFAF7", fontFamily: "Inter, system-ui, sans-serif" }}>
      <Header />

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "24px 16px" }}>
        {/* Page header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: "#0B0B0F", margin: 0 }}>Opportunities</h1>
            <p style={{ fontSize: 13, color: "#6B7280", margin: "2px 0 0" }}>All open opportunities</p>
          </div>
          <Link
            href="/opportunity/new"
            style={{ background: "#0B0B0F", color: "#FFFFFF", borderRadius: 6, padding: "8px 16px", fontSize: 13, fontWeight: 600, textDecoration: "none" }}
          >
            + New Opportunity
          </Link>
        </div>

        {/* Numbers lead */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 12,
            marginBottom: 20,
          }}
        >
          <div style={{ background: "#FFFFFF", borderRadius: 8, border: "1px solid #E5E5E7", padding: "14px 16px" }}>
            <div style={{ fontSize: 11, color: "#8E8E94", fontWeight: 600, marginBottom: 4 }}>TOTAL NET VALUE</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: "#0B0B0F" }}>{fmtCurrency(totalNet)}</div>
            <div style={{ fontSize: 11, color: "#6B7280" }}>{filtered.length} open {filtered.length === 1 ? "opp" : "opps"}</div>
          </div>
          {overrideCount > 0 && (
            <div style={{ background: "#FEF3C7", borderRadius: 8, border: "1px solid #FCD34D", padding: "14px 16px" }}>
              <div style={{ fontSize: 11, color: "#92400E", fontWeight: 600, marginBottom: 4 }}>OVERRIDDEN</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: "#92400E" }}>{overrideCount}</div>
              <div style={{ fontSize: 11, color: "#B45309" }}>forecast overrides active</div>
            </div>
          )}
          {/* Bucket distribution */}
          {bucketDist.filter((b) => b.count > 0).map((b) => (
            <div key={b.label} style={{ background: "#FFFFFF", borderRadius: 8, border: "1px solid #E5E5E7", padding: "14px 16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: FORECAST_HEX[b.label] }} />
                <span style={{ fontSize: 11, color: "#6B7280", fontWeight: 600, textTransform: "uppercase" }}>{b.label}</span>
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#0B0B0F" }}>{fmtCurrency(b.value)}</div>
              <div style={{ fontSize: 11, color: "#8E8E94" }}>{b.count} {b.count === 1 ? "opp" : "opps"}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
          <select
            value={filterBucket}
            onChange={(e) => setFilterBucket(e.target.value)}
            style={{ border: "1px solid #C9C9CC", borderRadius: 6, padding: "6px 10px", fontSize: 12, background: filterBucket ? `${FORECAST_HEX[filterBucket]}22` : "#FFFFFF" }}
          >
            <option value="">All Buckets</option>
            {BUCKETS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <input
            placeholder="Filter owner…"
            value={filterOwner}
            onChange={(e) => setFilterOwner(e.target.value)}
            style={{ border: "1px solid #C9C9CC", borderRadius: 6, padding: "6px 10px", fontSize: 12, minWidth: 130 }}
          />
          <input
            placeholder="Filter account…"
            value={filterAccount}
            onChange={(e) => setFilterAccount(e.target.value)}
            style={{ border: "1px solid #C9C9CC", borderRadius: 6, padding: "6px 10px", fontSize: 12, minWidth: 130 }}
          />
          {(filterBucket || filterOwner || filterAccount) && (
            <button
              onClick={() => { setFilterBucket(""); setFilterOwner(""); setFilterAccount(""); }}
              style={{ fontSize: 12, color: "#DC2626", background: "none", border: "1px solid #DC2626", borderRadius: 6, padding: "6px 12px", cursor: "pointer" }}
            >
              Clear
            </button>
          )}
        </div>

        {/* Table */}
        {error && (
          <div style={{ background: "#FEE2E2", color: "#991B1B", padding: 12, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{error}</div>
        )}
        {loading ? (
          <div style={{ fontSize: 13, color: "#8E8E94", padding: 20 }}>Loading…</div>
        ) : (
          <div style={{ background: "#FFFFFF", borderRadius: 8, border: "1px solid #E5E5E7", overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
                <thead>
                  <tr>
                    <th style={thSt}>Opportunity</th>
                    <th style={thSt}>Account</th>
                    <th style={thSt}>Owner</th>
                    <th style={{ ...thSt, textAlign: "right" }}>Net Value</th>
                    <th style={thSt}>Forecast</th>
                    <th style={{ ...thSt, textAlign: "center" }}>CP</th>
                    <th style={thSt}>Est. Close</th>
                    <th style={{ ...thSt, textAlign: "right" }}>Days Open</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={8} style={{ ...tdSt, textAlign: "center", color: "#8E8E94", padding: "24px" }}>
                        No open opportunities found.
                      </td>
                    </tr>
                  )}
                  {filtered.map((o) => (
                    <tr
                      key={o.id}
                      style={{ cursor: "pointer" }}
                      onClick={() => setLoc(`/opportunity/${o.id}`)}
                    >
                      <td style={tdSt}>
                        <div style={{ fontWeight: 600, color: "#0B0B0F" }}>{o.name}</div>
                        {o.line_count > 0 && (
                          <div style={{ fontSize: 11, color: "#8E8E94" }}>{o.line_count} line{o.line_count !== 1 ? "s" : ""}</div>
                        )}
                      </td>
                      <td style={tdSt}>{o.account_name ?? o.account_id}</td>
                      <td style={tdSt}>{o.primary_owner_name ?? "—"}</td>
                      <td style={{ ...tdSt, textAlign: "right", fontWeight: 700 }}>{fmtCurrency(o._netValue)}</td>
                      <td style={tdSt}>
                        <span
                          style={{
                            display: "inline-flex",
                            padding: "3px 8px",
                            borderRadius: 4,
                            fontSize: 11,
                            fontWeight: 700,
                            background: FORECAST_HEX[o._bucket] ?? "#6B7280",
                            color: "#FFFFFF",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {o._bucket}
                        </span>
                        {o.forecast_category_override && (
                          <span style={{ marginLeft: 4, fontSize: 9, color: "#B45309", fontWeight: 700 }}>OVR</span>
                        )}
                      </td>
                      <td style={{ ...tdSt, textAlign: "center" }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: "#525258" }}>CP{o.current_checkpoint ?? 1}</span>
                      </td>
                      <td style={tdSt}>
                        {o.est_close_date
                          ? new Date(o.est_close_date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                          : "—"}
                      </td>
                      <td style={{ ...tdSt, textAlign: "right", color: "#8E8E94" }}>
                        {daysOpen(o.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
